import React, { Fragment, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { load } from '../Navigation/Navigation.action';
import { setCurrentLanguageClass, setCurrentLanguageIso } from '../Language/Language.action';
import Language from '../Language/Language';

const Navigation = () => {
    const [isMenuOpened, setIsMenuOpened] = useState(false);
    const [activeIndex, setActiveIndex] = useState(null);
    // Kept apart from activeIndex, which drives the desktop dropdown. The mobile
    // accordion opens itself on the current section, and that must not make the
    // desktop menu drop a panel open on page load.
    const [mobileExpandedIndex, setMobileExpandedIndex] = useState(null);
    const contentLinks = useSelector((state) => state.navigation.contentLinks);
    const dictionaries = useSelector((state) => state.language.dictionaries);
    const dispatch = useDispatch();
    const [subMenuIndex, setSubMenuIndex] = useState(Array(4).fill(null));

    useEffect(() => {
        const fetchData = async () => {
            try {
                const firstHtml = document.querySelector('html');
                const iso = firstHtml.getAttribute('lang');
                dispatch(load());
                dispatch(setCurrentLanguageIso(iso));
                dispatch(setCurrentLanguageClass(`${iso}-icon`));
            } catch (error) {
                console.error('Error fetching languages:', error);
            }
        };

        fetchData();

    }, [dispatch]);

    const toggleExpandSubMenu = (index, level) => {
        let newSubMenuIndex = [...subMenuIndex];
        if (newSubMenuIndex[level] !== index) {
            newSubMenuIndex[level] = index;
        } else {
            newSubMenuIndex = Array(4).fill(null);
        }
        setSubMenuIndex(newSubMenuIndex);
    };

    const handleClick = () => {
        // Closing the menu drops back to the current section, so it reopens showing
        // where the visitor is rather than whatever they last browsed to.
        if (isMenuOpened) {
            setMobileExpandedIndex(currentSectionIndex === -1 ? null : currentSectionIndex);
            setSubMenuIndex(Array(4).fill(null));
        }
        setIsMenuOpened(!isMenuOpened);
    }

    const handleActiveMenu = (index) => {
        const isMenuOpened = activeIndex === index;
        const menuIndex = isMenuOpened ? null : index;
        setActiveIndex(menuIndex);
        setSubMenuIndex(Array(4).fill(null));


        //Change header state accourding to nav state, just for Desktop.
        const header = document.getElementById("header");
        const screenWidth = window.innerWidth;

        if (header && screenWidth > 1024) {
            if (isMenuOpened && window.scrollY < 96) {
                header.classList.remove("stickyHeader");
            } else {
                header.classList.add("stickyHeader");
            }
        }
    }

    const NestedLinksLevel1 = ({ menuName, nestedLinks }) => (
        <div className="grid-system grid-system-within-boundaries">
            <ul className="nested-links__level-1  grid-full-width">
                {menuName && <p>{menuName}</p>}
                {nestedLinks.map((nestedLink, index) => (
                    <li key={index} className="nested-link-item nested-link-item__level-1" onClick={() => toggleExpandSubMenu(index, 1)}>
                        <div className="navbar__item-link ">
                            <div className="navbar__item-link-title ">
                                <a
                                    className={"navbar__item-link-no-hero" + currentLinkClass(nestedLink)}
                                    href={nestedLink.url}
                                    aria-current={isCurrentPage(nestedLink) ? 'page' : undefined}
                                >{nestedLink.title}</a>
                                {(nestedLink.nestedLinks && nestedLink.nestedLinks.length > 0) && (
                                    <>
                                        {subMenuIndex[1] === index ? (
                                            <span className="navbar__item-arrow-opened" onClick={() => toggleExpandSubMenu(index, 1)} />
                                        ) : (
                                            <span className="navbar__item-arrow-default" onClick={() => toggleExpandSubMenu(index, 1)} />
                                        )}
                                    </>
                                )}
                            </div>
                            {subMenuIndex[1] === index && nestedLink.nestedLinks && nestedLink.nestedLinks.length > 0 && (
                                <div className="nested-links-container">
                                    <NestedLinksLevel2 nestedLinks={nestedLink.nestedLinks} />
                                </div>
                            )}
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );

    const NestedLinksLevel2 = ({ nestedLinks }) => (
        <>
            <ul className="nested-links__level-2">
                {nestedLinks.map((nestedLink, index) => (
                    <li key={index} className="nested-link-item nested-link-item__level-2">
                        <a
                            className={"navbar__item-link-no-hero" + currentLinkClass(nestedLink)}
                            href={nestedLink.url}
                            aria-current={isCurrentPage(nestedLink) ? 'page' : undefined}
                        >{nestedLink.title}</a>
                    </li>
                ))}
            </ul>
        </>
    );

    // A top level item is expandable when it has sub pages under it, no matter how it
    // is typed in the menu editor. Group headings carry no page of their own, so on
    // mobile a tap opens their submenu instead of following the link, which is what
    // the desktop menu already does with its dropdown.
    const hasNestedLinks = (link) => Array.isArray(link.nestedLinks) && link.nestedLinks.length > 0;

    // Menu links are a mix of relative and absolute addresses, with and without a
    // trailing slash, so both sides are resolved to "origin + path" before comparing.
    const normalizeUrl = (url) => {
        if (!url || url === '#' || typeof window === 'undefined') return null;

        try {
            const resolved = new URL(url, window.location.origin);
            const path = resolved.pathname.toLowerCase();

            return resolved.origin.toLowerCase() + (path.endsWith('/') ? path : `${path}/`);
        } catch {
            return null;
        }
    };

    const currentUrl = normalizeUrl(typeof window === 'undefined' ? null : window.location.pathname);
    const isCurrentPage = (link) => currentUrl !== null && normalizeUrl(link.url) === currentUrl;
    const containsCurrentPage = (link) =>
        (link.nestedLinks || []).some((nestedLink) => isCurrentPage(nestedLink) || containsCurrentPage(nestedLink));
    const currentLinkClass = (link) => (isCurrentPage(link) ? ' navbar__item-link-no-hero--current' : '');

    // The section holding the page being viewed, so the mobile menu opens on it and
    // the visitor can see where they are without hunting for it.
    const currentSectionIndex = contentLinks
        ? contentLinks.findIndex((link) => hasNestedLinks(link) && containsCurrentPage(link))
        : -1;

    useEffect(() => {
        setMobileExpandedIndex(currentSectionIndex === -1 ? null : currentSectionIndex);
    }, [currentSectionIndex]);

    const toggleMobileSection = (index) => {
        setMobileExpandedIndex(mobileExpandedIndex === index ? null : index);
        setSubMenuIndex(Array(4).fill(null));
    };

    return (
        <Fragment>
            {dictionaries && (
                <div>
                    <div className="navbar navbar-desktop">
                        <div id='navbar__menu-container' className={`navbar__menu-container`}>
                            <div className="navbar__menu">
                                <ul className="navbar__menu-links">
                                    {contentLinks && contentLinks.length > 0 && contentLinks.map((link, index) => (
                                        <li key={index} className={"navbar__item " + (activeIndex !== null ? (index === activeIndex ? 'navbar__item-active' : 'navbar__item-grey') : '')}>
                                            <div className="navbar__item-link">
                                                <a className="navbar__item-link-no-hero" href={link.url} onClick={() => handleActiveMenu(index)}>{link.title}</a>
                                                {activeIndex === index && link.nestedLinks && link.nestedLinks.length > 0 && (
                                                    <div className="nested-links-container container-shdow">
                                                        <NestedLinksLevel1 menuName={link.title} nestedLinks={link.nestedLinks} />
                                                    </div>
                                                )}
                                            </div>
                                            {(link.nestedLinks && link.nestedLinks.length > 0) && (
                                                <span className="navbar__item-arrow-default" onClick={() => handleActiveMenu(index)} />
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>


                    <div className="navbar navbar-portable">
                        <div className='navbar__hamburger' onClick={handleClick} >
                            <p>{isMenuOpened ? (dictionaries.find(item => item.key === "Close Menu")?.value ?? 'Close Menu') : (dictionaries.find(item => item.key === "Menu")?.value ?? 'Menu')}</p>
                            <span></span>
                        </div>
                        {isMenuOpened && (
                            <div id='navbar__menu-container' className={`navbar__menu-container`}>
                                <div className="navbar__menu-container__header">
                                    <div className="navbar__menu-container__language">
                                        <Language />
                                    </div>
                                    <div className="navbar__menu-container__header__switch" onClick={handleClick}>
                                        <p>{isMenuOpened ? (dictionaries.find(item => item.key === "Close Menu")?.value ?? 'Close Menu') : (dictionaries.find(item => item.key === "Menu")?.value ?? 'Menu')}</p>
                                        <span />
                                    </div>
                                </div>
                                <div className="navbar__menu">
                                    <ul className="navbar__menu-links">
                                        {contentLinks && contentLinks.length > 0 && contentLinks.map((link, index) => {
                                            const isExpandable = hasNestedLinks(link);
                                            const isExpanded = isExpandable && mobileExpandedIndex === index;

                                            return (
                                                <li key={index} className={"navbar__item " + (mobileExpandedIndex !== null ? (index === mobileExpandedIndex ? 'navbar__item-active' : 'navbar__item-grey') : '')}>
                                                    <div className="navbar__item-link">
                                                        {/* The whole row toggles, so the title and the arrow are one tap target. */}
                                                        <div
                                                            className={"navbar__item-link-title" + (isExpandable ? " navbar__item-link-title--expandable" : "")}
                                                            onClick={isExpandable ? () => toggleMobileSection(index) : undefined}
                                                        >
                                                            <a
                                                                className={"navbar__item-link-no-hero navbar__item-link-no-hero-base" + currentLinkClass(link)}
                                                                href={link.url}
                                                                aria-expanded={isExpandable ? isExpanded : undefined}
                                                                aria-current={isCurrentPage(link) ? 'page' : undefined}
                                                                onClick={isExpandable ? (event) => event.preventDefault() : undefined}
                                                            >{link.title}</a>
                                                            {isExpandable && (
                                                                <span className={"navbar__item-toggle" + (isExpanded ? " navbar__item-toggle--open" : "")} />
                                                            )}
                                                        </div>
                                                        {isExpanded && (
                                                            <div className="nested-links-container">
                                                                <NestedLinksLevel1 nestedLinks={link.nestedLinks} />
                                                            </div>
                                                        )}
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>

                                <div className="navbar__language">
                                    <Language />
                                </div>
                            </div>
                        )}
                    </div>
                </div>


            )}
        </Fragment >
    );
};

export default Navigation;
